import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  FormInputs,
  getPermissionsByRole,
  rolesWithLocationsShifts,
  initAdminPermissions,
  Role,
  schema,
  ROLE_OPTIONS,
  noDashboardAccessRoles,
  operatorRoles,
} from "./constants";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { useRecoilState, useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "src/firebase";
import { FormModeTypes } from "src/types/generic";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useGetLocationsList } from "src/api/locations";
import LocationSchedule from "./location-schedule";
import moment from "moment";
import { authState } from "src/store/auth";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ReactComponent as CalendarIcon } from "src/assets/svg-icons/calendar.svg";
import FileUploader, {
  IAttachmentFile,
} from "src/shared/components/file-uploader";
import { uploadEmployeeAttachment } from "src/utils/_firbaseStorage";
import { useGetCitiesList } from "src/api/general";
import ZoneSchedule from "./zone-schedule";
import DeleteIcon from "src/assets/svg-icons/delete.svg";
import { ServiceFormInputs } from "../../locations/add-edit-location/add-edit-service-popup";
import {
  useActivateEmployeeMutation,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
} from "src/api/employees/employees";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const getLocationServiceKey = (service: { type: string; name: string }) =>
  JSON.stringify([service.type, service.name]);

const AddEditEmployeePage = () => {
  const { id } = useParams();
  const mode: FormModeTypes = !id ? "add" : "edit";
  const navigate = useNavigate();
  const location = useLocation();
  const setNotifications = useSetRecoilState(notificationsState);
  const [auth] = useRecoilState(authState);
  const role = auth?.user?.roleType;
  const [adminPermissions, setAdminPermissions] =
    useState(initAdminPermissions);
  const [fullAdminPermissions, setFullAdminPermissions] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [selectedCitiesIds, setSelectedCitiesIds] = useState<string[]>([]);
  const [employeeData, setEmployeeData] = useState<any>();
  const [isLoadingFetching, setIsLoadingFetching] = useState(mode === "edit");
  const [isLoading, setIsLoading] = useState(false);
  const [openDate, setOpenDate] = useState(false);
  const [openStartDate, setOpenStartDate] = useState(false);
  const [openEndDate, setOpenEndDate] = useState(false);
  const [files, setFiles] = useState<IAttachmentFile[]>([]);
  const [nationalities, setNationalities] = useState<any[]>([]);

  const isComingFromActiveEmployeesPage = location.pathname.includes("active");

  const breadcrumbs = [
    <Link
      key="1"
      color={"#A1A5B7"}
      underline="hover"
      onClick={() => navigate(-1)}
      component="text"
      sx={{ cursor: "pointer" }}
    >
      {isComingFromActiveEmployeesPage ? "Active" : "Archived"} Accounts
    </Link>,

    <Typography key="2" color="#A1A5B7">
      {mode === "add" ? "Add New Employee" : "Edit Employee Account"}
    </Typography>,
  ];

  const methods = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      modeType: "regular",
      areaType: "location",
    },
  });
  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
    getValues,
  } = methods;

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
    replace: replaceLocations,
  } = useFieldArray({
    control,
    name: "locations",
  });

  const {
    fields: locationServicesFields,
    append: appendLocationService,
    remove: removeLocationService,
  } = useFieldArray({
    control,
    name: "locationsServices",
  });

  const handleAppendLocation = () => {
    appendLocationService({
      locationId: "",
      services: [],
    });
  };

  const handleRemoveLocation = (index: number) => {
    removeLocationService(index);
  };

  const roleType = watch("roleType");

  // APIS
  const { data: locationsData, isLoading: isLoadingLocations } =
    useGetLocationsList({
      type: "active",
      listAll: true,
    });
  const { data: citiesData, isLoading: isLoadingCities } = useGetCitiesList({
    type: "active",
  });
  const { mutate, isPending } = useAddEmployeeMutation();
  const { mutate: mutateUpdate, isPending: isPendingUpdate } =
    useUpdateEmployeeMutation();
  const { mutate: mutateActivate, isPending: isPendingActivate } =
    useActivateEmployeeMutation();

  const handleCheckboxChange = (
    moduleIndex: number,
    permissionIndex: number,
  ) => {
    setAdminPermissions((prev) => {
      const updatedPermissions = [...prev];
      updatedPermissions[moduleIndex].permissions[permissionIndex].checked =
        !updatedPermissions[moduleIndex].permissions[permissionIndex].checked;
      return updatedPermissions;
    });
  };

  // Function to handle full admin permission toggle
  const handleFullAdminPermission = () => {
    if (!fullAdminPermissions) {
      setAdminPermissions((prev) => {
        const updatedPermissions = [...prev];
        updatedPermissions.forEach((module) => {
          module.permissions.forEach((permission) => {
            permission.checked = true;
          });
        });
        return updatedPermissions;
      });
    } else {
      setAdminPermissions((prev) => {
        const updatedPermissions = [...prev];
        updatedPermissions.forEach((module) => {
          module.permissions.forEach((permission) => {
            permission.checked = false;
          });
        });
        return updatedPermissions;
      });
    }

    setFullAdminPermissions((prev) => !prev);
  };

  // Function to check if all checked keys are true
  const areAllCheckedTrue = () => {
    for (const item of adminPermissions) {
      for (const permission of item.permissions) {
        if (!permission.checked && permission.id !== "account_user_create") {
          return false;
        }
      }
    }
    // If all checked keys are true, return true
    return true;
  };

  const handleChangeLocationsSelected = (value: any[]) => {
    setSelectedLocations(value);
  };

  const handleChangeCitiesSelected = (value: any[]) => {
    setSelectedCitiesIds(value.map((city) => city.id));
  };

  const handleUnarchiveEmployee = () => {
    mutateActivate(id ?? "", {
      onSuccess: () => {
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "The employee has been activated successfully!",
          },
        ]);
        setIsLoading(false);
        navigate("/employees/active");
      },
      onError: (error) => {
        console.log({ error });
        const { message, response } = error;
        setIsLoading(false);
        setNotifications((prev) => [
          ...prev,
          {
            type: "error",
            message:
              response?.data?.message ??
              message ??
              "Oops! Something went wrong.",
          },
        ]);
      },
    });
  };

  // Fetch kafd categories enum
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchData = async () => {
      try {
        const docRef = doc(db, "settings", "sunmi_nationalities");

        unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const docData = docSnap.data();

            setNationalities([...(docData?.items || [])]);
          } else {
            console.warn("No such enum document found!");
          }
        });
      } catch (error) {
        console.error("Error fetching enum:", error);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleOpenDatePicker = () => setOpenDate(true);
  const handleCloseDatePicker = () => setOpenDate(false);
  const handleOpenStartDatePicker = () => setOpenStartDate(true);
  const handleCloseStartDatePicker = () => setOpenStartDate(false);
  const handleOpenEndDatePicker = () => setOpenEndDate(true);
  const handleCloseEndDatePicker = () => setOpenEndDate(false);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const hasAdminRole = roleType === "Admin";
    const notAdminRole = roleType && roleType !== "Admin";
    // validations
    const isAtLeastOneChecked = adminPermissions.some((module) => {
      return module.permissions.some((permission) => permission.checked);
    });
    if (!noDashboardAccessRoles.includes(roleType) && !isAtLeastOneChecked) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Select permissions",
        },
      ]);
      return;
    }

    if (
      operatorRoles.includes(roleType) &&
      selectedLocations.length === 0 &&
      (roleType !== "Zone Manager" || watch("areaType") === "location")
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Select operator locations",
        },
      ]);
      return;
    }

    if (
      ((roleType === "Zone Manager" && data.areaType === "city") ||
        roleType === "Operations Head") &&
      selectedCities.length === 0
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Select cities",
        },
      ]);
      return;
    }

    // max 4 locations for support mode
    if (
      rolesWithLocationsShifts.includes(roleType) &&
      data.modeType === "support" &&
      selectedLocations.length > 4
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Support mode is limited to 4 assigned locations",
        },
      ]);
      return;
    }

    const adminPermissionsToSend = adminPermissions.reduce((acc: any, item) => {
      const permissionsObj: any = {};
      item.permissions.forEach((permission) => {
        const createClientAccountUserPer =
          mode === "edit"
            ? employeeData.permissions?.clients?.account_user_create
            : false;

        permissionsObj[permission.id] =
          permission.id === "account_user_create"
            ? createClientAccountUserPer
            : permission.checked;
      });
      acc[item.name] = permissionsObj;
      return acc;
    }, {});

    setIsLoading(true);

    const existingFiles = files?.filter((f) => f.isUploaded) ?? [];

    const newFiles = files?.filter((f) => !f.isUploaded && f.file) ?? [];

    const uploadedAttachments = await Promise.all(
      newFiles.map((attachment) =>
        uploadEmployeeAttachment(`${id}`, attachment.file!),
      ),
    );

    const finalAttachments = [
      ...existingFiles.map((file) => ({
        // id: file.id,
        name: file.fileName,
        path: file.storagePath,
        url: file.downloadUrl,
        size: file.size,
        contentType: file.contentType,
        uploadedAt: file.uploadedAt,
      })),
      ...uploadedAttachments,
    ];

    console.log(uploadedAttachments);

    const dataToSend = {
      accountType: "regular",
      name: data.name,
      arabicName: data.arabicName,
      email: data.email,
      phone: data.phone,
      phone2: data.phone2 ?? "",
      employeeType: data.employeeType,
      nationalId: data.nationalId ?? "",
      nationalityId: data.nationality ?? "",
      dateOfBirth: data.dateOfBirth
        ? moment(data.dateOfBirth).format("YYYY-MM-DD")
        : null,
      startDate: data.startDate
        ? moment(data.startDate).format("YYYY-MM-DD")
        : null,
      endDate: data.endDate ? moment(data.endDate).format("YYYY-MM-DD") : null,
      isSupportMode: data.modeType === "support",
      roles: [
        ...(hasAdminRole ? ["Admin"] : []),
        ...(roleType === "Operator" ? ["Operator"] : []),
      ],

      ...(notAdminRole ? { locationType: data.roleType } : {}),

      ...(noDashboardAccessRoles.includes(roleType)
        ? {}
        : { permissions: adminPermissionsToSend }),

      ...(noDashboardAccessRoles.includes(roleType)
        ? {
            allowedLocationServices: data.locationsServices?.map((loc) => {
              const selectedServices = (loc.locationServicesOptions ?? []).filter(
                (service) =>
                  loc.services?.includes(getLocationServiceKey(service)),
              );
              return {
                locationId: loc.locationId,
                serviceTypes: selectedServices
                  .map((service) => service.type)
                  .filter(
                    (type, index, types) => types.indexOf(type) === index,
                  ),
                services: selectedServices.map(({ type, name }) => ({
                  type,
                  name,
                })),
              };
            }),
          }
        : {}),

      ...(rolesWithLocationsShifts.includes(roleType) ||
      (roleType === "Zone Manager" && data.areaType === "location")
        ? {
            allowedLocationsId: selectedLocations.map((loc) => loc?.id),
          }
        : {}),

      ...(rolesWithLocationsShifts.includes(roleType)
        ? {
            locationShifts: data.locations?.map((loc) => {
              return {
                locationId: loc.locationId,
                locationName: loc.locationName,
                shifts: loc.shifts?.map((shift) => {
                  return {
                    type: shift.shiftType,
                    from: shift.startTime
                      ? moment(shift.startTime).format("HH:mm")
                      : null,
                    to: shift.endTime
                      ? moment(shift.endTime).format("HH:mm")
                      : null,
                    repeat: shift.days ?? [],
                    startsOn: shift.startDate
                      ? moment(shift.startDate).startOf("day").valueOf()
                      : null,
                    endsOn: shift.endDate
                      ? moment(shift.endDate).endOf("day").valueOf()
                      : null,
                    flexibleHours: shift.flexibleHours
                      ? +shift.flexibleHours
                      : 0,
                  };
                }),
              };
            }),
          }
        : {}),

      ...(roleType === "Zone Manager"
        ? {
            zoneShift: {
              from: data.scheduleStartTime
                ? moment(data.scheduleStartTime).format("HH:mm")
                : null,
              to: data.scheduleEndTime
                ? moment(data.scheduleEndTime).format("HH:mm")
                : null,
              repeat: data.scheduleDays ?? [],
              startsOn: data.scheduleStartDate
                ? moment(data.scheduleStartDate).startOf("day").valueOf()
                : null,
              endsOn: data.scheduleEndDate
                ? moment(data.scheduleEndDate).startOf("day").valueOf()
                : null,
              flexibleHours: data.scheduleFlexibleHours
                ? +data.scheduleFlexibleHours
                : 0,
            },
          }
        : {}),

      ...(roleType === "Operations Head" ||
      (roleType === "Zone Manager" && watch("areaType") === "city")
        ? { allowedCityIds: selectedCitiesIds }
        : {}),

      attachments: finalAttachments ?? [],
    };
    console.log({ data, dataToSend });
    if (mode === "add") {
      mutate(dataToSend, {
        onSuccess: () => {
          setNotifications((prev) => [
            ...prev,
            {
              type: "success",
              message: "The employee has been added successfully!",
            },
          ]);
          setIsLoading(false);
          navigate("/employees/active");
        },
        onError: (error) => {
          console.log({ error });
          const { message, response } = error;
          setIsLoading(false);
          setNotifications((prev) => [
            ...prev,
            {
              type: "error",
              message:
                response?.data?.message ??
                message ??
                "Oops! Something went wrong.",
            },
          ]);
        },
      });
    } else {
      mutateUpdate(
        { ...dataToSend, employeeId: id },
        {
          onSuccess: () => {
            if (employeeData?.isUserActive === false) {
              handleUnarchiveEmployee();
              return;
            }
            setNotifications((prev) => [
              ...prev,
              {
                type: "success",
                message: "The employee has been updated successfully!",
              },
            ]);
            setIsLoading(false);
          },
          onError: (error) => {
            console.log({ error });
            const { message, response } = error;
            setIsLoading(false);
            setNotifications((prev) => [
              ...prev,
              {
                type: "error",
                message:
                  response?.data?.message ??
                  message ??
                  "Oops! Something went wrong.",
              },
            ]);
          },
        },
      );
    }
  };

  //Fetch Employee data
  useEffect(() => {
    if (id) {
      let unsubscribe: () => void;
      const docRef = doc(db, "users", `${id}`);

      unsubscribe = onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          const data = { ...doc.data() };
          setEmployeeData(data);
          console.log({ data });
          // Reset the form and permissions with employee data
          reset({
            modeType: data.isSupportMode ? "support" : "regular",
            areaType: data.allowedLocationsId?.length > 0 ? "location" : "city",
            name: data.name,
            arabicName: data.arabicName,
            email: data.email,
            nationalId: data.nationalId,
            nationality: data.nationalityId ?? "",
            dateOfBirth: data.dateOfBirth
              ? moment(data.dateOfBirth).toDate()
              : undefined,
            startDate: data.startDate
              ? moment(data.startDate).toDate()
              : undefined,
            endDate: data.endDate ? moment(data.endDate).toDate() : undefined,
            phone: data.phone,
            phone2: data.phone2,
            employeeType: data.employeeType ?? undefined,
            roleType: data.locationType ? data.locationType : "Admin",
            locations: (data.locationShifts ?? []).map((loc: any) => {
              return {
                locationId: loc.locationId,
                locationName: loc.locationName,
                shifts: loc.shifts.map((shift: any) => {
                  return {
                    shiftType: shift.type,
                    startTime: shift.from
                      ? moment(shift.from, "HH:mm").toDate()
                      : null,
                    endTime: shift.to
                      ? moment(shift.to, "HH:mm").toDate()
                      : null,
                    days: shift.repeat,
                    ends: shift.endsOn ? "yes" : "no",
                    startDate: shift.startsOn
                      ? moment(+shift.startsOn).toDate()
                      : null,
                    endDate: shift.endsOn
                      ? moment(+shift.endsOn).toDate()
                      : null,
                    flexibleHours: shift.flexibleHours ?? "0",
                  };
                }),
              };
            }),

            locationsServices: (data.allowedLocationServices ?? []).map(
              (loc: any) => {
                const selectedLocation = locationsData?.locations.find(
                  (item: any) => item.id === loc.locationId,
                );

                const locServices =
                  selectedLocation?.services?.map(
                    (service: ServiceFormInputs) => ({
                      type: service.type,
                      name: service.name,
                    }),
                  ) ?? [];
                const savedServices = loc.services ?? [];
                const locationServicesOptions = [
                  ...locServices,
                  ...savedServices.filter(
                    (service: { type: string; name: string }) =>
                      !locServices.some(
                        (option: { type: string; name: string }) =>
                          getLocationServiceKey(option) ===
                          getLocationServiceKey(service),
                      ),
                  ),
                ];
                return {
                  locationId: loc.locationId,
                  services: savedServices.map(getLocationServiceKey),
                  locationServicesOptions,
                };
              },
            ),
            scheduleStartTime: data.zoneShift?.from
              ? moment(data.zoneShift?.from, "HH:mm").toDate()
              : null,
            scheduleEndTime: data.zoneShift?.to
              ? moment(data.zoneShift?.to, "HH:mm").toDate()
              : null,
            scheduleStartDate: data.zoneShift?.startsOn
              ? moment(data.zoneShift?.startsOn).toDate()
              : null,
            scheduleEndDate: data.zoneShift?.endsOn
              ? moment(data.zoneShift?.endsOn).toDate()
              : null,
            scheduleDays: data.zoneShift?.repeat ?? [],
            scheduleFlexibleHours: data.zoneShift?.flexibleHours ?? "0",
          });

          const existingAttachments: IAttachmentFile[] = (
            data.attachments ?? []
          )?.map((att: any, index: number) => ({
            fileName: att.name,
            size: att.size,
            downloadUrl: att.url,
            storagePath: att.path,
            contentType: att.contentType,
            uploadedAt: att.uploadedAt,
            isUploaded: true,
          }));

          setFiles(existingAttachments ?? []);

          const allPermissionsCanAccess = getPermissionsByRole(
            data.locationType ?? "Admin",
          );
          const adminPermissionsData = allPermissionsCanAccess.map(
            (category) => {
              const permissions = category.permissions.map((permission) => {
                return {
                  ...permission,
                  checked: !!data.permissions?.[category.name]?.[permission.id],
                };
              });
              return {
                ...category,
                permissions: permissions,
              };
            },
          );

          setAdminPermissions(adminPermissionsData);

          const selectedLocationsArr = data.allowedLocationsId?.map(
            (locId: string) =>
              locationsData?.locations.find(
                (location: any) => location.id === locId,
              ),
          );
          setSelectedLocations(selectedLocationsArr ?? []);

          setSelectedCitiesIds(data.allowedCityIds ?? []);

          setIsLoadingFetching(false);
        } else {
          console.log("No such document!");
          setNotifications((prev) => [
            ...prev,
            {
              type: "error",
              message: "No such employee!",
            },
          ]);
        }
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [id, locationsData, citiesData]);

  // Effect to update fullAdminPermissions when permissions change
  useEffect(() => {
    const allChecked = areAllCheckedTrue();
    if (!allChecked) {
      setFullAdminPermissions(false);
    } else {
      setFullAdminPermissions(true);
    }
  }, [adminPermissions]);

  // append and remove shifts
  useEffect(() => {
    if (rolesWithLocationsShifts.includes(roleType)) {
      const locationsSchedules = watch("locations") ?? [];

      const updatedLocations = selectedLocations
        .filter(Boolean)
        .map((selectedLocation) => {
          // Keep existing location data if it already exists
          const existing = locationsSchedules.find(
            (loc) => loc.locationId === selectedLocation.id,
          );

          if (existing) {
            return existing;
          }

          // Otherwise create a new one
          return {
            locationId: selectedLocation.id,
            locationName: selectedLocation.nameEn,
            shifts: [
              {
                shiftType: "",
                startTime: null,
                endTime: null,
                days: [],
                startDate: null,
                endDate: null,
              },
            ],
          };
        });

      replaceLocations(updatedLocations);
    }
  }, [selectedLocations, roleType]);

  const selectedCities = (citiesData?.data ?? []).filter((city: any) =>
    selectedCitiesIds.includes(city.id),
  );

  const locationsServices = watch("locationsServices");

  const hasSelectedLocations = locationsServices?.some(
    (item) => !!item?.locationId,
  );

  const selectedLocationWithServicesIds = locationsServices?.map(
    (loc) => loc.locationId,
  );

  if (isLoadingFetching || isLoadingLocations || isLoadingCities) {
                return (
      <Box
        height={"80vh"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  console.log({ errors }, getValues());

  return (
    <Box p={{ xs: "15px", md: "30px" }} data-testid="add-edit-employee-page">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Typography fontSize={"22px"} mb={1} fontWeight={600}>
            {mode === "add" ? "Add New Employee" : "Edit Employee Account"}
          </Typography>
          {/* <RouterBreadcrumbs /> */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ color: "#5E6278" }}
          >
            {breadcrumbs}
          </Breadcrumbs>
          {/* Employee info */}
          <Box
            mt={3}
            p={{ xs: 2, md: 4 }}
            border={"1px solid #5B5B5B"}
            borderRadius={"10px"}
          >
            <Typography mb={3}>Employee info</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Name in English"
                      {...field}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="arabicName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Name in Arabic"
                      {...field}
                      error={!!errors.arabicName}
                      helperText={errors.arabicName?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.nationality}
                >
                  <InputLabel id="demo-simple-select-label">
                    Nationality
                  </InputLabel>
                  <Controller
                    name="nationality"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label=" Nationality"
                      >
                        {nationalities?.map((nationality) => (
                          <MenuItem
                            key={nationality.id}
                            value={nationality.id}
                          >
                            {nationality.labelAr}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.nationality?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="nationalId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label="National ID"
                      {...field}
                      error={!!errors.nationalId}
                      helperText={errors.nationalId?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="Email"
                      {...field}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      fullWidth
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      disabled={mode === "edit"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label="Mobile Number"
                      {...field}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="phone2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label="Secondary Mobile Number"
                      {...field}
                      error={!!errors.phone2}
                      helperText={errors.phone2?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.employeeType}
                >
                  <InputLabel id="demo-simple-select-label">
                    Employee Type
                  </InputLabel>
                  <Controller
                    name="employeeType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label=" Employee Type"
                      >
                        <MenuItem value={"permanent"}>Permanent</MenuItem>
                        <MenuItem value={"temporary"}>Temporary</MenuItem>
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.employeeType?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {/* date Of Birth */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Date Of Birth"
                      {...field}
                      maxDate={new Date()}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.dateOfBirth,
                          helperText: errors.dateOfBirth?.message,
                          onClick: handleOpenDatePicker,
                          onBlur: () => {
                            if (!openDate) {
                              field.onBlur();
                            }
                          },
                          inputProps: { readOnly: true },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                      format={"dd/MM/yyyy"}
                      open={openDate}
                      onOpen={handleOpenDatePicker}
                      onClose={handleCloseDatePicker}
                    />
                  )}
                />
              </Grid>
              {/* start date */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Joining Date"
                      {...field}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate?.message,
                          onClick: handleOpenStartDatePicker,
                          onBlur: () => {
                            if (!openStartDate) {
                              field.onBlur();
                            }
                          },
                          inputProps: { readOnly: true },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                      format={"dd/MM/yyyy"}
                      open={openStartDate}
                      onOpen={handleOpenStartDatePicker}
                      onClose={handleCloseStartDatePicker}
                    />
                  )}
                />
              </Grid>
              {/* end date */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="End Date"
                      {...field}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate?.message,
                          onClick: handleOpenEndDatePicker,
                          onBlur: () => {
                            if (!openEndDate) {
                              field.onBlur();
                            }
                          },
                          inputProps: { readOnly: true },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                      format={"dd/MM/yyyy"}
                      open={openEndDate}
                      onOpen={handleOpenEndDatePicker}
                      onClose={handleCloseEndDatePicker}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <FileUploader
                  value={files}
                  onChange={(files) => setFiles(files)}
                />
              </Grid>
            </Grid>
          </Box>
          {/* User Permissions */}
          <Box
            mt={3}
            p={{ xs: 2, md: 4 }}
            border={"1px solid #5B5B5B"}
            borderRadius={"10px"}
          >
            <Typography mb={3}>User Role And Permissions</Typography>
            <FormControl fullWidth size="small" error={!!errors.roleType}>
              <InputLabel id="demo-simple-select-label">Role</InputLabel>
              <Controller
                name="roleType"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Role"
                    sx={{ width: { xs: "100%", sm: "300px" } }}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      setAdminPermissions(getPermissionsByRole(value as Role));
                      setSelectedLocations([]);
                      removeLocation();
                      removeLocationService();
                      if (noDashboardAccessRoles.includes(value)) {
                        handleAppendLocation();
                      }
                    }}
                  >
                    {ROLE_OPTIONS[role as Role].map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.roleType?.message}
              </FormHelperText>
            </FormControl>
            {/* Mode Type */}
            {(role === "Super Admin" || role === "Admin") &&
            rolesWithLocationsShifts.includes(roleType) ? (
              <FormControl
                component="fieldset"
                error={!!errors.modeType}
                sx={{ mt: 2 }}
              >
                {/* <FormLabel component="legend">Mode</FormLabel> */}

                <Controller
                  name="modeType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    >
                      <FormControlLabel
                        value="regular"
                        control={<Radio />}
                        label="Regular Mode"
                      />

                      <FormControlLabel
                        value="support"
                        control={<Radio />}
                        label="Support Mode"
                      />
                    </RadioGroup>
                  )}
                />

                <FormHelperText>{errors.modeType?.message}</FormHelperText>
              </FormControl>
            ) : null}
            {/* Role Permissions */}
            {roleType && !noDashboardAccessRoles.includes(roleType) ? (
              <Box
                mt={3}
                p={{ xs: 1.5, md: 3 }}
                border={"1px solid #5B5B5B"}
                borderRadius={"10px"}
              >
                <>
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Box
                      bgcolor={"primary.main"}
                      borderRadius={"12px"}
                      height={"30px"}
                      width={"3px"}
                    />
                    <Checkbox
                      checked={fullAdminPermissions}
                      onChange={handleFullAdminPermission}
                    />
                    <Box>
                      <Typography fontSize={"14px"} fontWeight={600}>
                        Full Permission
                      </Typography>
                      {roleType === "Admin" && (
                        <Typography
                          color={"#A1A5B7"}
                          fontSize={"12px"}
                          fontWeight={600}
                        >
                          Accessibility in all dashboard modules
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Grid container spacing={3} mt={0}>
                    {adminPermissions.map((module, moduleIndex) => {
                      return (
                        <Grid item xs={12} sm={6} lg={3} key={module.name}>
                          <Stack
                            height={"100%"}
                            border={"1px solid #5B5B5B"}
                            p={3}
                            borderRadius={"10px"}
                          >
                            <Typography
                              fontWeight={600}
                              textTransform={"capitalize"}
                            >
                              {module.label}
                            </Typography>
                            {module.permissions
                              .filter((per) => per.id !== "account_user_create")
                              .map((permission, permissionIndex) => {
                                return (
                                  <FormControlLabel
                                    key={permission.id}
                                    control={
                                      <Checkbox
                                        checked={permission.checked}
                                        onChange={() =>
                                          handleCheckboxChange(
                                            moduleIndex,
                                            permissionIndex,
                                          )
                                        }
                                      />
                                    }
                                    label={
                                      <Typography style={{ fontSize: "13px" }}>
                                        {permission.label}
                                      </Typography>
                                    }
                                  />
                                );
                              })}
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              </Box>
            ) : null}

            {/* Operations Roles */}
            {operatorRoles.includes(roleType) ||
            roleType === "Operations Head" ? (
              <Box
                mt={3}
                p={{ xs: 1.5, md: 3 }}
                border={"1px solid #5B5B5B"}
                borderRadius={"10px"}
              >
                <>
                  {roleType === "Zone Manager" ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={3}
                      sx={{ mb: 1 }}
                    >
                      <FormLabel sx={{ whiteSpace: "nowrap" }}>
                        Assign By
                      </FormLabel>

                      <FormControl>
                        <Controller
                          name="areaType"
                          control={control}
                          render={({ field }) => (
                            <RadioGroup
                              row
                              {...field}
                              onChange={(event) => {
                                const value = event.target.value;
                                field.onChange(value);
                                removeLocation();
                                setSelectedLocations([]);
                                setSelectedCitiesIds([]);
                              }}
                            >
                              <FormControlLabel
                                value="location"
                                control={<Radio />}
                                label="Location"
                              />
                              <FormControlLabel
                                value="city"
                                control={<Radio />}
                                label="City"
                              />
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    </Box>
                  ) : null}

                  {/* to select locations */}
                  {roleType === "Operations Head" ||
                  (roleType === "Zone Manager" &&
                    watch("areaType") === "city") ? null : (
                    <>
                      {noDashboardAccessRoles.includes(roleType) ? (
                        <Box>
                          <Typography mb={2}>Assign Locations</Typography>
                          <Stack gap={3}>
                            {locationServicesFields.map(
                              (locServicesField, index) => {
                                const fieldLocationId =
                                  watch("locationsServices")?.[index]
                                    ?.locationId;
                                return (
                                  <Stack
                                    key={locServicesField.id}
                                    direction={"row"}
                                    gap={2}
                                    alignItems={"flex-start"}
                                    width="100%"
                                  >
                                    <FormControl
                                      size="small"
                                      sx={{ flex: "5 1 0", minWidth: 0 }}
                                    >
                                      <Controller
                                        name={`locationsServices.${index}.locationId`}
                                        control={control}
                                        render={({ field }) => (
                                          <Autocomplete
                                            // loading={true}
                                            size="small"
                                            options={
                                              (
                                                locationsData?.locations ?? []
                                              ).filter(
                                                (item: any) =>
                                                  !selectedLocationWithServicesIds?.includes(
                                                    item.id,
                                                  ) || item.id === field.value,
                                              ) ?? []
                                            }
                                            value={
                                              (
                                                locationsData?.locations ?? []
                                              ).find(
                                                (option: any) =>
                                                  option.id === field.value,
                                              ) ?? null
                                            }
                                            isOptionEqualToValue={(
                                              option,
                                              value,
                                            ) => option.id === value.id}
                                            getOptionLabel={(option: any) =>
                                              option?.nameEn
                                            }
                                            getOptionKey={(option: any) =>
                                              option.id
                                            }
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                error={
                                                  !!errors.locationsServices?.[
                                                    index
                                                  ]?.locationId
                                                }
                                                helperText={
                                                  errors.locationsServices?.[
                                                    index
                                                  ]?.locationId?.message
                                                }
                                                label={"Location"}
                                              />
                                            )}
                                            renderOption={(
                                              props,
                                              option: any,
                                            ) => (
                                              <li {...props} key={option.id}>
                                                <Typography>
                                                  {option.nameEn}
                                                </Typography>
                                              </li>
                                            )}
                                            onChange={(_, data) => {
                                              field.onChange(data?.id ?? null);

                                              setSelectedLocations((prev) => {
                                                const updated = [...prev];
                                                updated[index] = data ?? null;
                                                return updated;
                                              });

                                              // to set services options
                                              const services =
                                                data?.services
                                                  ?.map(
                                                    (
                                                      service: ServiceFormInputs,
                                                    ) => ({
                                                      type: service.type,
                                                      name: service.name,
                                                    }),
                                                  ) ?? [];

                                              setValue(
                                                `locationsServices.${index}.locationServicesOptions`,
                                                services,
                                              );
                                              // to reset services selected when location changes
                                              setValue(
                                                `locationsServices.${index}.services`,
                                                [],
                                              );
                                            }}
                                          />
                                        )}
                                      />
                                    </FormControl>

                                    <Stack
                                      direction="row"
                                      gap={1}
                                      alignItems="flex-start"
                                      sx={{ flex: "7 1 0", minWidth: 0 }}
                                    >
                                      <FormControl
                                        fullWidth
                                        size="small"
                                        error={
                                          !!errors.locationsServices?.[index]
                                            ?.services && !!fieldLocationId
                                        }
                                        sx={{ minWidth: 0, display: "block" }}
                                        disabled={!fieldLocationId}
                                      >
                                        <InputLabel id="demo-multiple-checkbox-label">
                                          Select Services
                                        </InputLabel>
                                        <Controller
                                          name={`locationsServices.${index}.services`}
                                          control={control}
                                          defaultValue={[]}
                                          render={({ field }) => (
                                            <Select
                                              {...field}
                                              fullWidth
                                              labelId="demo-multiple-checkbox-label"
                                              id="demo-multiple-checkbox"
                                              multiple
                                              input={
                                                <OutlinedInput
                                                  label={"Select Services"}
                                                />
                                              }
                                              renderValue={(selected) => (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  {selected.map((key) => {
                                                    const service = watch(
                                                      `locationsServices.${index}.locationServicesOptions`,
                                                    )?.find(
                                                      (option) =>
                                                        getLocationServiceKey(
                                                          option,
                                                        ) === key,
                                                    );
                                                    return (
                                                      <Chip
                                                        size="small"
                                                        key={key}
                                                        label={
                                                          service?.name ?? key
                                                        }
                                                      />
                                                    );
                                                  })}
                                                </Box>
                                              )}
                                            >
                                              {(
                                                watch("locationsServices")?.[
                                                  index
                                                ].locationServicesOptions ?? []
                                              ).map((service) => (
                                                <MenuItem
                                                  key={getLocationServiceKey(
                                                    service,
                                                  )}
                                                  value={getLocationServiceKey(
                                                    service,
                                                  )}
                                                >
                                                  <Checkbox
                                                    size="small"
                                                    checked={field.value?.includes(
                                                      getLocationServiceKey(
                                                        service,
                                                      ),
                                                    )}
                                                  />
                                                  {service.name}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          )}
                                        />
                                        <FormHelperText id="my-helper-text">
                                          {!!fieldLocationId &&
                                            errors.locationsServices?.[index]
                                              ?.services?.message}
                                        </FormHelperText>
                                      </FormControl>
                                      {locationServicesFields.length > 1 && (
                                        <IconButton
                                          onClick={() => {
                                            handleRemoveLocation(index);

                                            setSelectedLocations((prev) => {
                                              const updated = [...prev];
                                              updated.splice(index, 1);
                                              return updated;
                                            });
                                          }}
                                        >
                                          <img src={DeleteIcon} alt="" />
                                        </IconButton>
                                      )}
                                    </Stack>
                                  </Stack>
                                );
                              },
                            )}

                            {rolesWithLocationsShifts.includes(roleType) &&
                            watch("modeType") === "support" &&
                            selectedLocations.length >= 4 ? null : (
                              <Button
                                variant="outlined"
                                onClick={handleAppendLocation}
                                sx={{ width: "fit-content" }}
                              >
                                {hasSelectedLocations
                                  ? "Assign Another Location"
                                  : "Assign Location"}
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      ) : (
                        <Autocomplete
                          size="small"
                          multiple
                          id="tags-outlined"
                          options={locationsData?.locations ?? []}
                          value={selectedLocations}
                          disableCloseOnSelect
                          getOptionLabel={(option: any) => option?.nameEn}
                          getOptionKey={(option: any) => option.id}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8, padding: "0" }}
                                checked={selected}
                              />
                              <Typography>{option.nameEn}</Typography>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Locations"
                              placeholder="Locations"
                            />
                          )}
                          onChange={(event, value) => {
                            handleChangeLocationsSelected(value);
                          }}
                        />
                      )}
                    </>
                  )}

                  {watch("modeType") === "regular" ? (
                    <>
                      {locationFields.length > 0 && (
                        <Typography mt={3}>Shifts Schedule</Typography>
                      )}

                      {locationFields.map((location, locIndex) => {
                        return (
                          <LocationSchedule
                            key={location.id}
                            locIndex={locIndex}
                            operatorType={roleType}
                          />
                        );
                      })}
                    </>
                  ) : null}

                  {(watch("areaType") === "city" &&
                    roleType === "Zone Manager") ||
                  roleType === "Operations Head" ? (
                    <Autocomplete
                      size="small"
                      multiple
                      id="tags-outlined"
                      options={citiesData?.data ?? []}
                      value={selectedCities}
                      disableCloseOnSelect
                      getOptionLabel={(option: any) => option?.nameEn}
                      getOptionKey={(option: any) => option.id}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8, padding: "0" }}
                            checked={selected}
                          />
                          <Typography>{option.nameEn}</Typography>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cities"
                          placeholder="Cities"
                        />
                      )}
                      onChange={(event, value) => {
                        handleChangeCitiesSelected(value);
                      }}
                    />
                  ) : null}

                  {roleType === "Zone Manager" ? <ZoneSchedule /> : null}
                </>
              </Box>
            ) : null}
          </Box>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            mt={"50px"}
            gap={3}
          >
            <Button
              variant="outlined"
              sx={{ minWidth: { xs: "100px", md: "208px" } }}
              onClick={() =>
                navigate(
                  isComingFromActiveEmployeesPage
                    ? "/employees/active"
                    : "/employees/archived",
                )
              }
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ minWidth: { xs: "100px", md: "208px" } }}
              loading={isLoading}
            >
              {employeeData?.isUserActive === false
                ? "Save and Unarchive"
                : "Save"}
            </LoadingButton>
          </Stack>
        </FormProvider>
      </form>
    </Box>
  );
};

export default AddEditEmployeePage;
